/**
 * RuleTestPreview: what the draft rules would have scanned and skipped,
 * as distinct combinations of the fields the rules reference.
 */

import type {
  DreamScanRuleField,
  DreamScanRuleTestResponse,
  DreamScanRuleTestRow,
} from '@/api/generated/model'

function testRowLabel(row: DreamScanRuleTestRow, testedFields: readonly DreamScanRuleField[]): string {
  const parts = testedFields.map((field) => `${field}=${row[field] ?? '(missing)'}`)
  return parts.length === 0 ? 'all observed traces' : parts.join(' ')
}

export function RuleTestPreview({
  result,
  testedFields,
}: {
  result: DreamScanRuleTestResponse
  testedFields: readonly DreamScanRuleField[]
}): React.JSX.Element {
  return (
    <div className="space-y-1 rounded-sm border border-border p-3 text-xs">
      <p className="font-medium text-foreground">
        {result.sampled
          ? `These rules would scan ${String(result.scanned_traces)} of a recent sample of ${String(result.evaluated_traces)} observed traces (${String(result.total_traces)} in total).`
          : `These rules would scan ${String(result.scanned_traces)} of ${String(result.total_traces)} observed traces.`}
      </p>
      <p className="text-muted-foreground">
        The preview judges each trace on its own; the nightly scan decides once per
        conversation.
      </p>
      {result.scanned.map((row) => (
        <p key={`kept-${testRowLabel(row, testedFields)}`} className="font-mono text-muted-foreground">
          scanned ({row.traces}): {testRowLabel(row, testedFields)}
        </p>
      ))}
      {result.dropped.map((row) => (
        <p key={`drop-${testRowLabel(row, testedFields)}`} className="font-mono text-muted-foreground">
          skipped ({row.traces}): {testRowLabel(row, testedFields)}, by {row.dropped_by}
        </p>
      ))}
    </div>
  )
}
